import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { StatsResponseData } from '../models/stats';
import { PlantListData, PlantListResponseData } from '../models/plants';
import { PowerGenerationResponseData } from '../models/power-generation';
import { NotificationResponseData } from '../models/notification';
import { PowerCutResponse } from '../models/power-cut';
import { HomeConsumptionResponse } from '../models/home-consumption';
import { VideoGuideResponse } from '../models/video-guide';
import { FaqResponse } from '../models/faqs';
import { DosDontResponse } from '../models/dos-and-dont';
import { PlantDetail, PlantDataResponse } from '../models/plant';
import { InverterResponse } from '../models/inverter';
import { BatteryResponse } from '../models/battery';
import { SolarInfoResponse } from '../models/solar-info';
import { DataLoggerResponse } from '../models/data-logger';
import { ConnectedUserResponse } from '../models/connected-user';
import { PlantReportResponse } from '../models/plant-report';
import { StringInfoResponse } from '../models/string-info';
import { PlantOnlineRatioResponse } from '../models/plant-online-ratio';
import { ExploreResponse } from '../models/explore';
import { BaseUserService } from '../../base/services/base-user.service';
import { AddSecondaryUserResponse } from '../models/add-secondary-response';
import { DataLoggerTrendResponse } from '../models/datalogger-trends';
import { ActivatePlantResponse } from '../models/activate-plant-response';
import { PlantInfoResponse } from '../models/detailed-info';
import { LargePlantStats } from '../models/large-scale-plant';
import { MetersResponse } from '../models/meters-response';
import { SensorApiResponse } from '../models/sensors-response';
import { SiteInfoResponse } from '../models/site-info-response';
import { InverterStringResponse } from '../models/inverter-string-response';
import { LargeScalePowerGenerationResponse } from '../models/large-scale-power-generation';
import { LargeScaleTodayGenerationResponse } from '../models/large-scale-today-generation';
import { PlantPerformanceResponse } from '../models/pr-cuf-response';
import { LargeScalePlantGenerationResponse } from '../models/large-scale-plant-generation';
import { LargePlantsResponse } from '../models/large-plant-details';
import { TabCache } from '../models/enum';
import { StringDetailsResponse } from '../models/stringDetails';
import { StringMetricsResponse } from '../models/string-graph-details';

@Injectable({
  providedIn: 'root',
})
export class ProductService extends BaseUserService {
  plantList = new BehaviorSubject<PlantListData | null>(null);
  selectedPlant = new BehaviorSubject<PlantDetail | null>(null);
  
   private readonly TTL = 5 * 60 * 1000;
  private inverterCache?: TabCache<InverterStringResponse>;
  private metersCache?: TabCache<MetersResponse>;
  private sensorsCache?: TabCache<SensorApiResponse>;
  private siteInfoCache?: TabCache<SiteInfoResponse>;
 
  constructor() {
    super();
  }

  clean() {
    this.plantList.next(null);
    this.selectedPlant.next(null);
  }

  /**
   *
   * @param productCategory
   * @param includeFlag
   * @returns
   */
  fetchFaqs(
    productCategory: string,
    includeFlag: string,
  ): Observable<FaqResponse> {
    return this.http.get<FaqResponse>(
      this.env.externalUrls.product.baseUrl + 'util/getFAQ',
      {
        params: {
          product_category: productCategory,
          include_flag: includeFlag,
        },
      },
    );
  }

  /**
   *
   * @returns Observable<VideoGuideResponse>
   */
  fetchVideoGuide(): Observable<VideoGuideResponse> {
    return this.http.get<VideoGuideResponse>(
      this.env.externalUrls.product.baseUrl + 'util/getVideoGuide',
    );
  }

  /**
   *
   * @param productCategory
   * @param includeFlag
   * @returns
   */
  fetchDosAndDont(
    productCategory: string,
    includeFlag: string,
  ): Observable<DosDontResponse> {
    return this.http.get<DosDontResponse>(
      this.env.externalUrls.product.baseUrl + 'util/getDosDonts',
      {
        params: {
          product_category: productCategory,
          include_flag: includeFlag,
        },
      },
    );
  }

  /**
   *
   * @param plantId return plant id of the plant
   * @returns
   */
  getStats(plantId: string): Observable<StatsResponseData> {
    return this.http.get<StatsResponseData>(
      this.env.externalUrls.product.baseUrl + 'plants/' + plantId + '/statsV1',
    );
  }

  /**
   * Fetch Plant Notifications
   * @param plantId
   * @param userId
   * @returns
   */
  getNotifications(plantId: string, userId: string) {
    return this.http.get<NotificationResponseData>(
      this.env.externalUrls.user.baseUrl +
      '/users/system-alert-plant?plantId=' +
      plantId +
      '&userId=' +
      userId,
    );
  }
  /**
   *
   * @param userId
   * @returns
   */
  getPlantDetail(plantId: string): Observable<PlantDataResponse> {
    return this.http
      .get<PlantDataResponse>(
        this.env.externalUrls.product.baseUrl + 'plants/getPlantV1/' + plantId,
      )
      .pipe(
        tap((response: PlantDataResponse) => {
          this.selectedPlant.next(response.data);
        }),
      );
  }

  /**
   *
   * @param plantId
   * @returns
   */
  getInverterDetail(plantId: string): Observable<InverterResponse> {
    return this.http.get<InverterResponse>(
      this.env.externalUrls.product.baseUrl + 'plants/getInverter/' + plantId,
    );
  }
  /**
   *
   * @param plantId
   * @returns
   */

  getBatteryDetail(plantId: string): Observable<BatteryResponse> {
    return this.http.get<BatteryResponse>(
      this.env.externalUrls.product.baseUrl + 'plants/getBattery/' + plantId,
    );
  }

  /**
   *
   * @param plantId
   * @returns
   */

  getSolarDetail(plantId: string): Observable<SolarInfoResponse> {
    return this.http.get<SolarInfoResponse>(
      this.env.externalUrls.product.baseUrl + 'plants/getSolarInfo/' + plantId,
    );
  }

  /**
   *
   * @param plantId
   * @returns
   */

  getDataLogger(plantId: string): Observable<DataLoggerResponse> {
    return this.http.get<DataLoggerResponse>(
      this.env.externalUrls.product.baseUrl + 'plants/getDataLogger/' + plantId,
    );
  }

  /**
   *
   * @param plantId
   * @returns
   */

  getConnectedUser(plantId: string): Observable<ConnectedUserResponse> {
    return this.http.get<ConnectedUserResponse>(
      this.env.externalUrls.product.baseUrl +
      'plants/getConnectedUsers/' +
      plantId,
    );
  }

  /**
   *
   * @param userId
   * @param plantId
   * @returns
   */
  removeConnectedUser(
    userId: string,
    plantId: string,
  ): Observable<any> {
    return this.http.delete<HttpResponse<any>>(
      this.env.externalUrls.product.baseUrl +
      'plants/' +
      plantId +
      '/remove_user/' +
      userId,
    );
  }

  /**
   *
   * @param plantId
   * @param timeFlag
   * @param timeZone
   * @returns
   */
  downloadPlantReport(
    plantId: string,
    timeFlag: number,
    timeZone: string,
  ): Observable<PlantReportResponse> {
    let params = new HttpParams();

    params = params.set('timeFlag', timeFlag);
    params = params.set('timezone', timeZone);
    return this.http.get<PlantReportResponse>(
      this.env.externalUrls.product.baseUrl +
      'plants/' +
      plantId +
      '/downloadReportV1',
      { params },
    );
  }

  /**
   *
   * @param plantId
   * @returns
   */
  deactivatePlant(plantId: string): Observable<PlantReportResponse> {
    return this.http.delete<PlantReportResponse>(
      this.env.externalUrls.product.baseUrl + 'plants/delete/' + plantId,
    );
  }

  /**
   *
   * @param id can be plant id or installer id
   * @returns Observable StatsResponseData
   */
  getPowerGenerationTrends(
    id: string,
    date: number | null,
    startDate: number | null,
    endDate: number | null,
    year: number | null,
    type?: string | undefined,
  ): Observable<PowerGenerationResponseData> {
    let params = new HttpParams();

    if (date) {
      params = params.set('date', date);
    }

    if (year) {
      params = params.set('year', year);
    }

    if (startDate && endDate) {
      params = params.set('start_date', startDate);
      params = params.set('end_date', endDate);
    }

    if (type) {
      params = params.set('installerId', id);
      params = params.set('inverterType', type);
      return this.http.get<PowerGenerationResponseData>(
        this.env.externalUrls.system_integration.baseUrl + '/power-generations',
        { params },
      );
    } else {
      return this.http.get<PowerGenerationResponseData>(
        this.env.externalUrls.product.baseUrl +
        'plants/' +
        id +
        '/power-generations',
        { params },
      );
    }
  }

  /**
   *
   * @param plantId
   * @returns Observable StatsResponseData
   */
  getPowerConsumptionTrends(
    id: string,
    date: number | null,
    startDate: number | null,
    endDate: number | null,
    year: number | null,
    type?: string,
  ): Observable<HomeConsumptionResponse> {
    let params = new HttpParams();

    if (date) {
      params = params.set('date', date);
    }

    if (year) {
      params = params.set('year', year);
    }

    if (startDate && endDate) {
      params = params.set('start_date', startDate);
      params = params.set('end_date', endDate);
    }

    if (type) {
      params = params.set('installerId', id);
      params = params.set('inverterType', type);
      return this.http.get<HomeConsumptionResponse>(
        this.env.externalUrls.system_integration.baseUrl + '/home-consumption',
        { params },
      );
    } else {
      return this.http.get<HomeConsumptionResponse>(
        this.env.externalUrls.product.baseUrl +
        'plants/' +
        id +
        '/home-consumption',
        { params },
      );
    }
  }

  /**
   *
   * @param plantId
   * @returns Observable StatsResponseData
   */
  getPowerCutGeneration(
    id: string,
    date: number | null,
    startDate: number | null,
    endDate: number | null,
    year: number | null,
    type?: string,
  ): Observable<PowerCutResponse> {
    let params = new HttpParams();

    if (date) {
      params = params.set('date', date);
    }

    if (year) {
      params = params.set('year', year);
    }

    if (startDate && endDate) {
      params = params.set('start_date', startDate);
      params = params.set('end_date', endDate);
    }
    if (type) {
      params = params.set('installerId', id);
      params = params.set('inverterType', type);
      return this.http.get<PowerCutResponse>(
        this.env.externalUrls.system_integration.baseUrl + '/power-cut',
        { params },
      );
    } else {
      return this.http.get<PowerCutResponse>(
        this.env.externalUrls.product.baseUrl + 'plants/' + id + '/power-cut',
        { params },
      );
    }
  }

  /**
   *
   * @returns get plant list using login user detail
   */
  getPlantList(
    userId: string,
  ): Observable<HttpResponse<PlantListResponseData>> {
    return this.http
      .get<PlantListResponseData>(
        this.env.externalUrls.product.baseUrl + 'plants/getAllV1/' + userId,
        { observe: 'response' },
      )
      .pipe(
        tap((response: HttpResponse<PlantListResponseData>) => {
          if (response.status == 204) {
            this.plantList.next({
              primaryPlant: [],
              secondaryPlant: [],
            });
          } else if (response.body) {
            this.plantList.next(response.body.data);
          }
        }),
      );
  }

  /**
   *
   * @param plantId
   * @param date
   * @returns
   */
  getStringDetail(
    plantId: string,
    date: number,
  ): Observable<StringInfoResponse> {
    return this.http.get<StringInfoResponse>(
      this.env.externalUrls.product.baseUrl +
      'plants/' +
      plantId +
      '/stringDetailsTrends?date=' +
      date,
    );
  }
  
  fetchStringTableDetails(plantId:string,date:number):Observable<StringDetailsResponse>{
   return this.http.get<StringDetailsResponse>(
    this.env.externalUrls.product.baseUrl+ 'plants/' +plantId +'/stringDetails?date='+date
   )
  }
  fetchStringGraphDetails(plantId:string,date:number,metricType:string):Observable<StringMetricsResponse>{
    return this.http.get<StringMetricsResponse>(
      this.env.externalUrls.product.baseUrl+'plants/' +plantId +'/stringDetailsTrendsV2?date='+date+'&metric='+metricType
    )
  }
  /**
   *
   * @param userId
   * @returns
   */
  getPlantOnlineRatio(plantId: string): Observable<PlantOnlineRatioResponse> {
    return this.http.get<PlantOnlineRatioResponse>(
      this.env.externalUrls.product.baseUrl +
      'plants/plantOnlineRatio?plantId=' +
      plantId,
    );
  }

  /**
   *
   * @returns
   */
  fetchExplorePost(): Observable<ExploreResponse> {
    return this.http.get<ExploreResponse>(
      this.env.externalUrls.product.baseUrl + 'util/getExplore',
    );
  }
  /**
   * Add secondary User
   */

  inviteSecondaryUser(
    data: { email: string; name: string; phone: string },
    plantId: string,
  ): Observable<AddSecondaryUserResponse> {
    return this.http.post<AddSecondaryUserResponse>(
      this.env.externalUrls.product.baseUrl +
      'plants/' +
      plantId +
      '/addSecondaryUser',
      data,
    );
  }

  /**
   *
   * @param plantId
   * @returns
   */

  getDataLoggerTrends(
    plantId: string,
    date: number,
  ): Observable<DataLoggerTrendResponse> {
    return this.http.get<DataLoggerTrendResponse>(
      this.env.externalUrls.product.baseUrl +
      'plants/' +
      plantId +
      '/data-logger-wifi-trends?date=' +
      date,
    );
  }

  /**
   * activate plant
   */

  activatePlant(plantId: string): Observable<ActivatePlantResponse> {
    return this.http.put<ActivatePlantResponse>(
      this.env.externalUrls.product.baseUrl + 'plants/activate/' + plantId,
      {},
    );
  }

  getDetailedInfo(plantID: string | undefined): Observable<PlantInfoResponse> {
    const Id = plantID
    let url = this.env.externalUrls.product.baseUrl + 'plants/' + Id + '/getDetailedInfo'
    return this.http.get<PlantInfoResponse>(url)
  }
  getLargePlantDetails():Observable<LargePlantsResponse>{
     let url=this.env.externalUrls.product.baseUrl + 'largePlants/getLargePlantsList'
     return this.http.get<LargePlantsResponse>(url)
  }
  getLargePlantsStats(plantId: string): Observable<LargePlantStats> {
      const cache = this.storageService.getObject('largeScaleStats');
 
    if (
      
      cache &&
      cache?.plantId === plantId &&
      Date.now() - cache.lastFetched < this.TTL
    ) {
      return of(cache.data);
    }
    let url = this.env.externalUrls.product.baseUrl + 'largePlants/getLargePlantsStats/' + plantId;
    return this.http.get<LargePlantStats>(url).pipe(tap(data=>{
         const largeScaleStatsCache = { data, lastFetched: Date.now(), plantId };
         this.storageService.setObject('largeScaleStats',largeScaleStatsCache)
    }))
  }
  
  
 getInverterStringData(plantId: string, forceRefresh = false) {
    const cache = this.inverterCache;
 
    if (
      !forceRefresh &&
      cache &&
      cache?.plantId === plantId &&
      Date.now() - cache.lastFetched < this.TTL
    ) {
      return of(cache.data);
    }
 
    const url =
      this.env.externalUrls.product.baseUrl +
      'largePlants/getAllInvertersStringData/' +
      plantId;
 
    return this.http.get<InverterStringResponse>(url).pipe(
      tap(data => {
        this.inverterCache = { data, lastFetched: Date.now(), plantId , lastVisited: cache?.lastVisited ?? Date.now()};
      })
    );
  }
 
  
  getMetersData(plantId: string, forceRefresh = false) {
    const cache = this.metersCache;
 
    if (
      !forceRefresh &&
      cache &&
      cache?.plantId === plantId &&
      Date.now() - cache.lastFetched < this.TTL
    ) {
     
      return of(cache.data);
    }
  
    const url =
      this.env.externalUrls.product.baseUrl +
      'largePlants/getMetersData/' +
      plantId;
 
    return this.http.get<MetersResponse>(url).pipe(
      tap(data => {
        this.metersCache = { data, lastFetched: Date.now(), plantId, lastVisited: cache?.lastVisited ?? Date.now() };
      })
    );
  }
 
 
  getSensorsData(plantId: string, forceRefresh = false) {
    const cache = this.sensorsCache;
 
    if (
      !forceRefresh &&
      cache &&
      cache?.plantId === plantId &&
      Date.now() - cache.lastFetched < this.TTL
    ) {
      return of(cache.data);
    }
 
    const url =
      this.env.externalUrls.product.baseUrl +
      'largePlants/getSensorData/' +
      plantId;
 
    return this.http.get<SensorApiResponse>(url).pipe(
      tap(data => {
        this.sensorsCache = { data, lastFetched: Date.now(), plantId ,
           lastVisited: cache?.lastVisited ?? Date.now()
        };
      })
    );
  }
 
 
  getSiteInfoData(plantId: string, forceRefresh = false) {
    const cache = this.siteInfoCache;
 
    if (
      !forceRefresh &&
      cache &&
      cache?.plantId === plantId &&
      Date.now() - cache.lastFetched < this.TTL
    ) {
      return of(cache.data);
    }
 
    const url =
      this.env.externalUrls.product.baseUrl +
      'largePlants/getSiteInfoData/' +
      plantId;
 
  
        return this.http.get<SiteInfoResponse>(url).pipe(
      tap(data => {
        this.siteInfoCache = { data, lastFetched: Date.now(), plantId ,
           lastVisited: cache?.lastVisited ?? Date.now()
        };
      })
    );
  }
  getPowerGenerationData(plantId: string): Observable<LargeScalePowerGenerationResponse> {
     const cache = this.storageService.getObject('largeScalePowerGeneration');
 
    if (
      
      cache &&
      cache?.plantId === plantId &&
      Date.now() - cache.lastFetched < this.TTL
    ) {
      return of(cache.data);
    }
    let url = this.env.externalUrls.product.baseUrl + 'largePlants/getTotalPowerGenerationGraph/' + plantId;
    return this.http.get<LargeScalePowerGenerationResponse>(url).pipe(tap(data=>{
         const largeScalePowerGeneration = { data, lastFetched: Date.now(), plantId };
         this.storageService.setObject('largeScalePowerGeneration',largeScalePowerGeneration)
    }))
  
  }
  getTodayGenerationData(plantId: string): Observable<LargeScaleTodayGenerationResponse> {
     const cache = this.storageService.getObject('largeScaleTodayGeneration');
 
    if (
      
      cache &&
      cache?.plantId === plantId&&
      Date.now() - cache.lastFetched < this.TTL
    ) {
      return of(cache.data);
    }
    let url = this.env.externalUrls.product.baseUrl + 'largePlants/getTodayInverterGenerationGraph/' + plantId;
    return this.http.get<LargeScaleTodayGenerationResponse>(url).pipe(tap(data=>{
         const largeScaleTodayGeneration = { data, lastFetched: Date.now(), plantId };
         this.storageService.setObject('largeScaleTodayGeneration',largeScaleTodayGeneration)
    }))
  
  }
  getPrCufData(plantId: string): Observable<PlantPerformanceResponse> {
     const cache = this.storageService.getObject('largeScalePrCuf');
 
    if (
      
      cache &&
      cache?.plantId === plantId &&
      Date.now() - cache.lastFetched < this.TTL
    ) {
      return of(cache.data);
    }
    let url = this.env.externalUrls.product.baseUrl + 'largePlants/getPrCufGraph/' + plantId;
    return this.http.get<PlantPerformanceResponse>(url).pipe(tap(data=>{
         const largeScalePrCuf = { data, lastFetched: Date.now(), plantId };
         this.storageService.setObject('largeScalePrCuf',largeScalePrCuf)
    }))
  
  }

  getPlantGenerationData(plantId: string ): Observable<LargeScalePlantGenerationResponse> {
     const cache = this.storageService.getObject('largeScalePlantGeneration');
 
    if (
      
      cache &&
      cache?.plantId === plantId &&
      Date.now() - cache.lastFetched < this.TTL
    ) {
      return of(cache.data);
    }
    let url = this.env.externalUrls.product.baseUrl + 'largePlants/getPlantGenerationGraph/' + plantId;
    return this.http.get<LargeScalePlantGenerationResponse>(url).pipe(tap(data=>{
         const largeScalePlantGeneration = { data, lastFetched: Date.now(), plantId};
         this.storageService.setObject('largeScalePlantGeneration',largeScalePlantGeneration)
    }))
  
  }
 
   isInverterExpiredOnRevisit(plantId: string): boolean {
    return (
      !this.inverterCache ||
      this.inverterCache?.plantId !== plantId ||
      Date.now() - this.inverterCache.lastVisited >= this.TTL
    );
  }
   isMetersExpiredOnRevisit(plantId: string): boolean {
    return (
      !this.metersCache ||
      this.metersCache?.plantId !== plantId ||
      Date.now() - this.metersCache.lastVisited >= this.TTL
    );
  }
   isSensorsExpiredOnRevisit(plantId: string): boolean {
    return (
      !this.sensorsCache ||
      this.sensorsCache?.plantId !== plantId ||
      Date.now() - this.sensorsCache.lastVisited >= this.TTL
    );
  }
  isSiteInfoExpiredOnRevisit(plantId: string): boolean {
    return (
      !this.siteInfoCache ||
      this.siteInfoCache?.plantId !== plantId ||
      Date.now() - this.siteInfoCache.lastVisited >= this.TTL
    );
  }
 
  markInverterVisited() {
    if (this.inverterCache) {
      this.inverterCache.lastVisited = Date.now();
    }
  }
  markMetersVisited() {
    if (this.metersCache) {
      this.metersCache.lastVisited = Date.now();
    }
  }
  markSensorsVisited() {
    if (this.sensorsCache) {
      this.sensorsCache.lastVisited = Date.now();
    }
  }
  markSiteInfoVisited() {
    if (this.siteInfoCache) {
      this.siteInfoCache.lastVisited = Date.now();
    }
  }
}
  